import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Image,
  Typography,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "../../../config";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const StatusToggle = ({ form, name, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 12px",
      background: "#fafafa",
      borderRadius: 8,
      border: "1px solid #f0f0f0",
    }}
  >
    <Text style={{ fontSize: 13, color: "#595959" }}>{label}</Text>
    <Form.Item name={name} valuePropName="checked" noStyle>
      <Switch size="small" />
    </Form.Item>
  </div>
);

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: "#8c8c8c",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 8,
      marginTop: 4,
    }}
  >
    {children}
  </div>
);

const ProductEditAdmin = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [newSize, setNewSize] = useState("");
  const [displayImage, setDisplayImage] = useState(null);
  const [, setProductName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const base = global.config.APIURL.BaseURL;

    Promise.all([
      axios.get(base + "api/ProductAdminEdit/GetAllProductUnits", { headers }),
      axios.get(base + "api/ProductAdminEdit/GetAllCategoriesForDropdown", { headers }),
      axios.get(base + `api/ProductAdminEdit/GetProductDetailForEdit?productID=${productId}`, { headers }),
      axios.get(base + `api/Product/GetProductImages?ProdID=${productId}`, { headers }),
    ])
      .then(([unitsRes, catsRes, detailRes, imagesRes]) => {
        setUnits(unitsRes.data || []);
        setCategories(catsRes.data || []);

        const d = detailRes.data;
        setProductName(d.ProductName || "");
        form.setFieldsValue({
          ProductName: d.ProductName,
          DesignNo: d.DesignNo,
          SalesRate: d.SalesRate,
          QuantityInBox: d.QuantityInBox,
          NumberofColor: d.NumberofColor,
          MOQ: d.MOQ,
          HSN: d.HSN,
          Detail: d.Detail,
          IsActive: d.IsActive,
          IsInStock: d.IsInStock,
          IsListedPublicly: d.IsListedPublicly,
          SellingSingleColor: d.SellingSingleColor,
          IsPublished: d.IsPublished,
          Tag1: d.Tag1,
          Tag2: d.Tag2,
          UnitID: d.UnitID,
          CategoryIDs: d.CategoryIDs,
        });
        setSizes(d.Sizes || []);

        const imgs = imagesRes.data || [];
        const display = imgs.find((i) => i.IsDisplayImage) || imgs[0];
        if (display) setDisplayImage(display.ImageUrl);
      })
      .catch(() => message.error("Failed to load product details."))
      .finally(() => setLoading(false));
  }, [productId, form]);

  const handleAddSize = () => {
    const trimmed = newSize.trim();
    if (!trimmed) return;
    if (sizes.find((s) => s.Size === trimmed)) {
      message.warning("Size already added.");
      return;
    }
    setSizes((prev) => [...prev, { ProductSizeID: 0, Size: trimmed }]);
    setNewSize("");
  };

  const handleRemoveSize = (sizeValue) => {
    setSizes((prev) => prev.filter((s) => s.Size !== sizeValue));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.post(
        global.config.APIURL.BaseURL + "api/ProductAdminEdit/UpdateProductDetail",
        {
          ProductID: Number(productId),
          ProductName: values.ProductName,
          DesignNo: values.DesignNo || "",
          SalesRate: values.SalesRate || 0,
          QuantityInBox: values.QuantityInBox || 0,
          NumberofColor: values.NumberofColor || 0,
          MOQ: values.MOQ || 0,
          HSN: values.HSN || "",
          Detail: values.Detail || "",
          IsActive: values.IsActive,
          IsInStock: values.IsInStock,
          IsListedPublicly: values.IsListedPublicly,
          SellingSingleColor: values.SellingSingleColor,
          IsPublished: values.IsPublished,
          Tag1: values.Tag1 || "",
          Tag2: values.Tag2 || "",
          UnitID: values.UnitID,
          CategoryIDs: values.CategoryIDs || [],
          Sizes: sizes.map((s) => s.Size),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Product updated successfully!");
    } catch (err) {
      if (err?.response) {
        message.error("Failed to update product. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      styles={{ body: { padding: "16px 20px" } }}
      title={
        <Space size={4}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/AdminAllProducts")}
            type="text"
            size="small"
          />
          <span style={{ fontWeight: 600, fontSize: 15 }}>Edit Product</span>
        </Space>
      }
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      }
    >
      <Form form={form} layout="vertical" size="small" style={{ "--form-item-margin": "10px" }}>

        {/* ── TOP: Image + Identity ─────────────────────────────── */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "flex-start" }}>
          {/* Reference image */}
          <div
            style={{
              flexShrink: 0,
              width: 120,
              height: 120,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #f0f0f0",
              background: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {displayImage ? (
              <Image
                src={displayImage}
                width={120}
                height={120}
                style={{ objectFit: "cover" }}
                preview={{ mask: "View" }}
              />
            ) : (
              <div style={{ textAlign: "center", color: "#d9d9d9" }}>
                <PictureOutlined style={{ fontSize: 28 }} />
                <div style={{ fontSize: 11, marginTop: 4 }}>No image</div>
              </div>
            )}
          </div>

          {/* Name + DesignNo + numeric fields */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Row gutter={[12, 0]}>
              <Col xs={24} sm={14}>
                <Form.Item
                  name="ProductName"
                  label={<span style={{ fontSize: 12 }}>Product Name <span style={{ color: "#ff4d4f" }}>*</span></span>}
                  rules={[{ required: true, message: "Required" }]}
                  style={{ marginBottom: 10 }}
                >
                  <Input style={{ fontWeight: 600 }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item
                  name="DesignNo"
                  label={<span style={{ fontSize: 12 }}>Design No</span>}
                  style={{ marginBottom: 10 }}
                >
                  <Input />
                </Form.Item>
              </Col>

              {/* Numeric row */}
              <Col xs={12} sm={6}>
                <Form.Item
                  name="SalesRate"
                  label={<span style={{ fontSize: 12 }}>Rate (₹)</span>}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber style={{ width: "100%" }} min={0} precision={2} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="MOQ"
                  label={<span style={{ fontSize: 12 }}>MOQ</span>}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="QuantityInBox"
                  label={<span style={{ fontSize: 12 }}>Qty / Box</span>}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="NumberofColor"
                  label={<span style={{ fontSize: 12 }}>Colors</span>}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber style={{ width: "100%" }} min={0} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── ROW: Unit + HSN + Categories ─────────────────────── */}
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={8} md={6}>
            <Form.Item
              name="UnitID"
              label={<span style={{ fontSize: 12 }}>Unit <span style={{ color: "#ff4d4f" }}>*</span></span>}
              rules={[{ required: true, message: "Required" }]}
              style={{ marginBottom: 10 }}
            >
              <Select placeholder="Select" showSearch optionFilterProp="children">
                {units.map((u) => (
                  <Option key={u.UnitID} value={u.UnitID}>{u.UnitName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Form.Item
              name="HSN"
              label={<span style={{ fontSize: 12 }}>HSN Code</span>}
              style={{ marginBottom: 10 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              name="CategoryIDs"
              label={<span style={{ fontSize: 12 }}>Categories <span style={{ color: "#ff4d4f" }}>*</span></span>}
              rules={[{ required: true, message: "Required" }]}
              style={{ marginBottom: 10 }}
            >
              <Select mode="multiple" placeholder="Select categories" optionFilterProp="children" showSearch>
                {categories.map((c) => (
                  <Option key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ── Description ──────────────────────────────────────── */}
        <Form.Item
          name="Detail"
          label={<span style={{ fontSize: 12 }}>Description</span>}
          style={{ marginBottom: 10 }}
        >
          <TextArea rows={3} style={{ resize: "none" }} />
        </Form.Item>

        {/* ── Tags + Sizes ─────────────────────────────────────── */}
        <Row gutter={[12, 0]}>
          <Col xs={12} sm={6}>
            <Form.Item
              name="Tag1"
              label={<span style={{ fontSize: 12 }}>Tag 1</span>}
              style={{ marginBottom: 10 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item
              name="Tag2"
              label={<span style={{ fontSize: 12 }}>Tag 2</span>}
              style={{ marginBottom: 10 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ marginBottom: 10 }}>
              <SectionLabel>Sizes</SectionLabel>
              <Space wrap size={4} style={{ marginBottom: 6 }}>
                {sizes.map((s) => (
                  <Tag
                    key={s.Size}
                    closable
                    onClose={() => handleRemoveSize(s.Size)}
                    style={{
                      background: "#f0f5ff",
                      borderColor: "#adc6ff",
                      color: "#2f54eb",
                      borderRadius: 4,
                      fontWeight: 500,
                    }}
                  >
                    {s.Size}
                  </Tag>
                ))}
                {sizes.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>None</Text>
                )}
              </Space>
              <Space size={6}>
                <Input
                  placeholder="S, M, L, XL…"
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  onPressEnter={handleAddSize}
                  style={{ width: 130 }}
                  size="small"
                />
                <Button icon={<PlusOutlined />} onClick={handleAddSize} size="small">
                  Add
                </Button>
              </Space>
            </div>
          </Col>
        </Row>

        {/* ── Status toggles ───────────────────────────────────── */}
        <SectionLabel>Status</SectionLabel>
        <Row gutter={[8, 8]}>
          {[
            { name: "IsActive", label: "Active" },
            { name: "IsInStock", label: "In Stock" },
            { name: "IsListedPublicly", label: "Listed Publicly" },
            { name: "SellingSingleColor", label: "Single Color" },
            { name: "IsPublished", label: "Published" },
          ].map(({ name, label }) => (
            <Col key={name} xs={12} sm={8} md={6} lg={4}>
              <StatusToggle form={form} name={name} label={label} />
            </Col>
          ))}
        </Row>
      </Form>

      <div style={{ textAlign: "right", marginTop: 16, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </Card>
  );
};

export default ProductEditAdmin;
